import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "../../../contexts/LanguageContext";
import { useDropzone } from "react-dropzone";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import {
  generateExamFromFile,
  generateExamAuto,
  generateExamFromMistakes,
  getHistory,
  getAllHistory,
  getFavorites,
  deleteExamRecord,
  removeFavorite,
  getVocabularyUnits,
} from "../../../services/examService";
import { createExamGenSession } from "../../../services/settingsService";
import { Eye, EyeOff } from "lucide-react";
import styles from "./SpanishExam.module.css";
import spain from "../../../images/spain.png";

const SUBJECT_META = {
  spanish: { flag: spain, color: "#E91E63", key: "spanish" },
  german: { flag: "🇩🇪", color: "#1565C0", key: "german" },
  english: { flag: "🇬🇧", color: "#6A1B9A", key: "english" },
};

const DELE_LEVELS = [
  { id: "A1", label: "A1 – Beginner" },
  { id: "A2", label: "A2 – Elementary" },
  { id: "B1", label: "B1 – Intermediate" },
  { id: "B2", label: "B2 – Upper-Int." },
  { id: "C1", label: "C1 – Advanced" },
  { id: "C2", label: "C2 – Mastery" },
];

const LEVELS = [
  { id: "beginner-1", label: "Beginner 1" },
  { id: "beginner-2", label: "Beginner 2" },
  { id: "beginner-3", label: "Beginner 3" },
  { id: "intermediate-1", label: "Intermediate 1" },
  { id: "intermediate-2", label: "Intermediate 2" },
  { id: "intermediate-3", label: "Intermediate 3" },
  { id: "advanced-1", label: "Advanced 1" },
  { id: "advanced-2", label: "Advanced 2" },
  { id: "advanced-3", label: "Advanced 3" },
];

function levelGroup(id) {
  if (id.startsWith("A") || id.startsWith("beginner")) return "beginner";
  if (id.startsWith("B") || id.startsWith("intermediate"))
    return "intermediate";
  return "advanced";
}

// 5-minute IP-bound session cache for exam generation
const EXAM_GEN_SESSION_KEY = "examGenTokenUnlocked";

function getUnlockedExamToken() {
  try {
    const raw = sessionStorage.getItem(EXAM_GEN_SESSION_KEY);
    if (!raw) return null;
    const { expiresAt, token } = JSON.parse(raw);
    if (Date.now() < expiresAt) {
      return token;
    }
    sessionStorage.removeItem(EXAM_GEN_SESSION_KEY);
    return null;
  } catch {
    return null;
  }
}

function unlockExamGenSession(token, expiresInMs = 5 * 60 * 1000) {
  sessionStorage.setItem(
    EXAM_GEN_SESSION_KEY,
    JSON.stringify({ expiresAt: Date.now() + expiresInMs, token })
  );
}

function clearExamGenSession() {
  sessionStorage.removeItem(EXAM_GEN_SESSION_KEY);
}

/* ─────────────────────────────────────────────────────────────────────────── */
export default function SpanishExam() {
  const subject = "spanish";
  const { t } = useLanguage();
  const navigate = useNavigate();
  const meta = SUBJECT_META[subject];

  const [tab, setTab] = useState("upload"); // 'upload' | 'auto'
  const [autoTab, setAutoTab] = useState("level"); // 'level' | 'mistakes'
  const [selectedLevel, setSelectedLevel] = useState("");
  const [uploadedFile, setUploadedFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dashTab, setDashTab] = useState("history");
  const [history, setHistory] = useState([]);
  const [allHistory, setAllHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [generationFeedback, setGenerationFeedback] = useState(null);
  const [genProgress, setGenProgress] = useState(0);
  const progressTimerRef = useRef(null);

  // Clear progress interval on unmount
  useEffect(() => {
    return () => {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
      }
    };
  }, []);

  // Password Modal State
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [examPassword, setExamPassword] = useState("");
  const [showExamPassword, setShowExamPassword] = useState(false);
  const [modalStatus, setModalStatus] = useState("idle"); // idle, verifying, error, success
  const passwordInputRef = useRef(null);

  // Delete Password Modal State
  const [deleteModalRecordId, setDeleteModalRecordId] = useState(null);
  const [deletePassword, setDeletePassword] = useState("");
  const [showDeletePassword, setShowDeletePassword] = useState(false);
  const [deleteModalStatus, setDeleteModalStatus] = useState("idle");
  const deletePasswordInputRef = useRef(null);

  // Generation error modal
  const [genError, setGenError] = useState(null); // null | 'busy' | 'other'

  // Vocabulary units
  const [vocabUnits, setVocabUnits] = useState([]);

  /* ── fetch history ────────────────────────────────────────────────────── */
  useEffect(() => {
    (async () => {
      const cacheKey = `exam-history-${subject}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        setHistory(JSON.parse(cached));
        setLoadingHistory(false);
      } else {
        setLoadingHistory(true);
      }

      try {
        const data = await getHistory(subject);
        setHistory(data);
        localStorage.setItem(cacheKey, JSON.stringify(data));
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingHistory(false);
      }
    })();
  }, [subject]);

  /* ── fetch ALL history for score trend (no 20-record cap) ───────────── */
  useEffect(() => {
    (async () => {
      try {
        const data = await getAllHistory(subject);
        setAllHistory(data);
      } catch (err) {
        console.error(err);
      }
    })();
  }, [subject]);

  /* ── fetch vocabulary units ──────────────────────────────────────────── */
  useEffect(() => {
    getVocabularyUnits(subject).then(setVocabUnits).catch(() => setVocabUnits([]));
  }, [subject]);

  /* ── recent history (latest 10 for the visible history list) ───────── */
  const recentHistory = useMemo(() => {
    if (!history?.length) return [];
    return [...history]
      .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
      .slice(0, 20);
  }, [history]);

  /* ── chart data – uses ALL records so the trend shows full history ───── */
  const chartData = useMemo(() => {
    const source = allHistory.length > 0 ? allHistory : history;
    if (!source?.length) return [];
    return [...source]
      .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
      .map((item) => {
        const d = new Date(item.createdAt);
        return {
          id: item.id,
          date: d.toLocaleDateString([], { month: "short", day: "numeric" }),
          score: item.score,
          total: item.total,
          percentage:
            item.total > 0 ? Math.round((item.score / item.total) * 100) : 0,
          fullDate: d.toLocaleString(),
        };
      });
  }, [allHistory, history]);

  /* ── dropzone ─────────────────────────────────────────────────────────── */
  const onDrop = useCallback((accepted) => {
    if (accepted[0]) setUploadedFile(accepted[0]);
  }, []);
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "image/*": [".jpg", ".jpeg", ".png", ".webp"],
    },
    maxSize: 20 * 1024 * 1024,
    multiple: false,
  });

  /* ── generate ─────────────────────────────────────────────────────────── */
  const canGenerate =
    (tab === "upload" && uploadedFile) ||
    (tab === "auto" && autoTab === "level" && selectedLevel) ||
    (tab === "auto" && autoTab === "mistakes");

  const executeGenerate = async (tokenOrPassword) => {
    if (!canGenerate) return;
    const authCredential = tokenOrPassword || examPassword || getUnlockedExamToken() || "";
    setLoading(true);
    setGenProgress(8);
    setGenerationFeedback({
      type: "progress",
      message: "AI is analyzing document and extracting key vocabulary...",
    });

    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
    }
    progressTimerRef.current = setInterval(() => {
      setGenProgress((prev) => {
        let step = 0;
        if (prev < 25) step = 3;
        else if (prev < 55) step = 1.8;
        else if (prev < 80) step = 1.1;
        else if (prev < 92) step = 0.5;
        else if (prev < 96) step = 0.15;
        return Math.min(96, +(prev + step).toFixed(1));
      });
    }, 200);

    try {
      let exam;
      if (tab === "upload" && uploadedFile) {
        exam = await generateExamFromFile(uploadedFile, subject, authCredential);
      } else if (tab === "auto" && autoTab === "level" && selectedLevel) {
        exam = await generateExamAuto(subject, selectedLevel, authCredential);
      } else if (tab === "auto" && autoTab === "mistakes") {
        exam = await generateExamFromMistakes(subject, authCredential);
      }
      if (exam) {
        if (progressTimerRef.current) {
          clearInterval(progressTimerRef.current);
          progressTimerRef.current = null;
        }
        setGenProgress(100);
        setGenerationFeedback({
          type: "success",
          message: "Your exam is ready — Unit Vocab and Vocabulary Bank are now available.",
        });
        window.setTimeout(() => {
          navigate(`/study/spanish/exam/${exam.id}`);
        }, 1100);
      }
    } catch (err) {
      if (progressTimerRef.current) {
        clearInterval(progressTimerRef.current);
        progressTimerRef.current = null;
      }
      setGenProgress(0);
      console.error(err);
      const status = err.response?.status;
      const msg = err.response?.data?.error || err.message || "";
      if (status === 401 || status === 403) {
        clearExamGenSession();
        setExamPassword("");
        setShowExamPassword(false);
        setModalStatus("idle");
        setShowPasswordModal(true);
        setGenerationFeedback(null);
        return;
      }
      if (
        status === 503 ||
        msg.toLowerCase().includes("503") ||
        msg.toLowerCase().includes("busy") ||
        msg.toLowerCase().includes("demand")
      ) {
        setGenError("busy");
      } else {
        setGenError("other");
      }
      setGenerationFeedback(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async () => {
    setModalStatus("verifying");
    const result = await createExamGenSession(examPassword);
    if (!result || !result.valid) {
      setModalStatus("error");
    } else {
      unlockExamGenSession(result.token, result.expiresInMs);
      setModalStatus("success");
      setTimeout(() => {
        setShowPasswordModal(false);
        setModalStatus("idle");
        executeGenerate(result.token);
      }, 800);
    }
  };

  const handleGenerateClick = () => {
    const cachedToken = getUnlockedExamToken();
    if (cachedToken) {
      executeGenerate(cachedToken);
    } else {
      setExamPassword("");
      setShowExamPassword(false);
      setModalStatus("idle");
      setShowPasswordModal(true);
    }
  };

  const handleDeleteClick = (recordId) => {
    setDeleteModalRecordId(recordId);
    setDeletePassword("");
    setShowDeletePassword(false);
    setDeleteModalStatus("idle");
  };

  const handleDeleteSubmit = async () => {
    setDeleteModalStatus("deleting");
    const success = await deleteExamRecord(deleteModalRecordId, deletePassword);
    if (!success) {
      setDeleteModalStatus("error");
    } else {
      setDeleteModalStatus("success");
      setTimeout(() => {
        setHistory((prev) => prev.filter((i) => i.id !== deleteModalRecordId));
        setAllHistory((prev) => prev.filter((i) => i.id !== deleteModalRecordId));
        setDeleteModalRecordId(null);
        setDeleteModalStatus("idle");
      }, 1000);
    }
  };

  /* ── render ───────────────────────────────────────────────────────────── */
  const portalTarget = typeof document !== "undefined" ? document.body : null;

  return (
    <div className={styles.page}>
      {/* ── Compact header ── */}
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.flag}>
            {typeof meta.flag === "string" &&
              meta.flag.startsWith("/static/") ? (
              <img src={meta.flag} alt={meta.key} width={28} height={28} />
            ) : (
              meta.flag
            )}
          </div>
          <div>
            <h1 className={styles.title}>{t(`exam.${meta.key}`)} Exam</h1>
          </div>
        </div>
        <button className={styles.backBtn} onClick={() => navigate("/study")}>
          ← {t("common.back")}
        </button>
      </header>

      {/* ── Two-column body ── */}
      <div className={styles.body}>
        {/* ────── LEFT: Generator ────── */}
        <div className={styles.generator}>
          {/* Mode switcher */}
          <div className={styles.tabRow}>
            <button
              className={`${styles.tab} ${tab === "upload" ? styles.tabActive : ""}`}
              onClick={() => setTab("upload")}
            >
              📄 {t("exam.generate_from_file")}
            </button>
            <button
              className={`${styles.tab} ${tab === "auto" ? styles.tabActive : ""}`}
              onClick={() => setTab("auto")}
            >
              🤖 {t("exam.auto_generate")}
            </button>
          </div>

          {/* Upload panel */}
          {tab === "upload" && (
            <div className={styles.panel}>
              <h3 className={styles.panelTitle}>{t("exam.upload_title")}</h3>
              {/* <p className={styles.panelDesc}>{t('exam.upload_desc')}</p> */}

              <div
                {...getRootProps()}
                className={`${styles.dropzone}
                  ${isDragActive ? styles.dropzoneActive : ""}
                  ${uploadedFile ? styles.dropzoneDone : ""}`}
              >
                <input {...getInputProps()} />
                {uploadedFile ? (
                  <div className={styles.fileReady}>
                    <span className={styles.fileIcon}>✅</span>
                    <span className={styles.fileName}>{uploadedFile.name}</span>
                    <span className={styles.fileSize}>
                      ({(uploadedFile.size / 1024).toFixed(0)} KB)
                    </span>
                    <button
                      className={styles.removeFile}
                      onClick={(e) => {
                        e.stopPropagation();
                        setUploadedFile(null);
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div className={styles.dropPrompt}>
                    <span className={styles.dropIcon}>📂</span>
                    <p className={styles.dropText}>
                      {isDragActive ? "Drop it here!" : t("exam.upload_desc")}
                    </p>
                    <button className={styles.uploadbtn}>
                      {t("exam.upload_btn")}
                    </button>
                    <small className={styles.dropHint}>
                      {t("exam.upload_hint")}
                    </small>
                  </div>
                )}
              </div>

              <div className={styles.aiInfo}>
                <span>🤖</span>
                <p style={{ fontSize: "0.75rem" }}>
                  AI will scan your file and generate a personalised 4-section
                  exam: Multiple Choice, Fill-in, True/False &amp; Translation.
                </p>
              </div>
            </div>
          )}

          {/* Auto-generate panel */}
          {tab === "auto" && (
            <div className={styles.panel}>
              {/* Sub-tabs */}
              <div className={styles.tabRow} style={{ marginBottom: 0 }}>
                <button
                  className={`${styles.tab} ${autoTab === "level" ? styles.tabActive : ""}`}
                  onClick={() => setAutoTab("level")}
                  style={{ fontSize: "0.78rem" }}
                >
                  ⇡ By Level
                </button>
                <button
                  className={`${styles.tab} ${autoTab === "mistakes" ? styles.tabActive : ""}`}
                  onClick={() => setAutoTab("mistakes")}
                  style={{ fontSize: "0.78rem" }}
                >
                  ⚔️ From Mistakes
                </button>
              </div>

              {autoTab === "level" && (
                <>
                  <h3 className={styles.panelTitle}>{t("exam.levels")}</h3>
                  <p className={styles.panelDesc}>
                    Choose a proficiency level to auto-generate a structured
                    exam.
                  </p>
                  <div className={styles.levelsGrid}>
                    {(subject === "spanish" ? DELE_LEVELS : LEVELS).map(
                      (lv) => (
                        <button
                          key={lv.id}
                          className={`${styles.levelBtn}
                          ${selectedLevel === lv.id ? styles.levelBtnActive : ""}
                          ${styles[`level_${levelGroup(lv.id)}`]}`}
                          onClick={() => setSelectedLevel(lv.id)}
                        >
                          {lv.label}
                        </button>
                      ),
                    )}
                  </div>
                </>
              )}

              {autoTab === "mistakes" && (
                <>
                  <h3 className={styles.panelTitle}>Generate from Mistakes</h3>
                  <p className={styles.panelDesc}>
                    Target your weak points. AI will analyse your exam history
                    and create a focused review.
                  </p>
                  <div className={styles.aiInfo}>
                    <span>🤖</span>
                    <p>
                      AI analyses all past records to produce a unique 4-section
                      assessment targeting your individual learning gaps.
                    </p>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Generate CTA */}
          <button
            className={`${styles.generateBtn} ${loading ? styles.generateBtnLoading : ""}`}
            onClick={handleGenerateClick}
            disabled={!canGenerate || loading}
          >
            {loading ? (
              <>
                <span className="spinner" /> {t("exam.generating")}
              </>
            ) : (
              <>✨ {t("exam.generate_btn")}</>
            )}
          </button>
        </div>

        {/* ────── RIGHT: Dashboard ────── */}
        <div className={styles.dashboard}>
          <div className={styles.dashHeader}>
            <p className={styles.dashTitle}>{t("exam.dashboard_title")}</p>
            <div className={styles.dashTabs}>
              <button
                className={`${styles.dashTab} ${dashTab === "history" ? styles.dashTabActive : ""}`}
                onClick={() => setDashTab("history")}
              >
                📋 {t("exam.history_title")}
              </button>
              <button
                className={`${styles.dashTab} ${dashTab === "vocab" ? styles.dashTabActive : ""}`}
                onClick={() => setDashTab("vocab")}
              >
                📖 Unit Vocab
              </button>
              <button
                className={`${styles.dashTab} ${dashTab === "favorites" ? styles.dashTabActive : ""}`}
                onClick={() => setDashTab("favorites")}
              >
                ⭐ {t("exam.favorites_title")}
              </button>
            </div>
          </div>

          <div className={styles.dashBody}>
            {/* History tab */}
            {dashTab === "history" &&
              (loadingHistory ? (
                <div className={styles.empty}>{t("common.loading")}</div>
              ) : history.length === 0 ? (
                <div className={styles.empty}>
                  <span style={{ fontSize: "2rem" }}>📋</span>
                  <p>{t("exam.history_empty")}</p>
                </div>
              ) : (
                <>
                  {/* Trend chart */}
                  <div className={styles.trendChart}>
                    <div className={styles.trendChartTitle}>📈 Score Trend</div>
                    <div className={styles.trendChartScroll} aria-label="Score trend history">
                      <div
                        className={styles.trendChartCanvas}
                        style={{ "--trend-point-count": chartData.length }}
                      >
                        <ResponsiveContainer width="100%" height={100}>
                          <LineChart
                            data={chartData}
                            margin={{ top: 6, right: 12, left: -28, bottom: 0 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              vertical={false}
                              stroke="#e0e0e0"
                            />
                            <XAxis
                              dataKey="date"
                              tick={{ fontSize: 9, fill: "#aaa" }}
                              tickLine={false}
                              axisLine={false}
                              dy={6}
                              interval={Math.max(0, Math.ceil(chartData.length / 10) - 1)}
                            />
                            <YAxis
                              tick={{ fontSize: 9, fill: "#aaa" }}
                              tickLine={false}
                              axisLine={false}
                              domain={[0, 100]}
                            />
                            <Tooltip
                              contentStyle={{
                                borderRadius: "8px",
                                fontSize: "10px",
                                border: "none",
                                boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                              }}
                              formatter={(val, _, props) => [
                                `${val}% (${props.payload.score}/${props.payload.total})`,
                                "Score",
                              ]}
                            />
                            <Line
                              type="monotone"
                              dataKey="percentage"
                              stroke="var(--color-primary, #4caf50)"
                              strokeWidth={2}
                              dot={({ cx, cy, payload }) => (
                                <circle
                                  cx={cx}
                                  cy={cy}
                                  r={3}
                                  fill={payload.percentage === 100 ? "#D4AF37" : "var(--color-primary, #4caf50)"}
                                />
                              )}
                              activeDot={({ cx, cy, payload }) => (
                                <circle
                                  cx={cx}
                                  cy={cy}
                                  r={4}
                                  strokeWidth={0}
                                  fill={payload.percentage === 100 ? "#D4AF37" : "var(--color-primary, #4caf50)"}
                                />
                              )}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  </div>

                  {/* Grouped history records */}
                  {Object.entries(
                    recentHistory.reduce((acc, item) => {
                      const title = item.exam?.title || "Exam";
                      if (!acc[title]) acc[title] = [];
                      acc[title].push(item);
                      return acc;
                    }, {}),
                  ).map(([title, items]) => (
                    <div key={title}>
                      <div className={styles.groupTitle}>{title}</div>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "0.35rem",
                          marginBottom: "0.75rem",
                        }}
                      >
                        {items.slice(0, 5).map((item, idx) => (
                          <div key={item.id} className={styles.historyRow}>
                            {/* Rank */}
                            <span className={styles.historyRank}>
                              #{idx + 1}
                            </span>
                            {/* Date */}
                            <span className={styles.historyDate}>
                              <span>
                                {new Date(item.createdAt).toLocaleDateString(
                                  [],
                                  { month: "2-digit", day: "2-digit" },
                                )}
                              </span>
                              <span className={styles.historyTimeSub}>
                                {new Date(item.createdAt).toLocaleTimeString(
                                  [],
                                  { hour: "2-digit", minute: "2-digit" },
                                )}
                              </span>
                            </span>
                            {/* Progress bar */}
                            <div className={styles.historyProgressWrap}>
                              <div
                                className={styles.historyProgressBar}
                                style={{
                                  width: `${item.total > 0 ? (item.score / item.total) * 100 : 0}%`,
                                  "--score-percentage": item.total > 0 ? (item.score / item.total) * 100 : 0,
                                }}
                              />
                            </div>
                            {/* Score */}
                            <span className={styles.historyScore}>
                              {item.score}/{item.total}
                            </span>
                            {/* Time */}
                            {item.timeElapsed > 0 && (
                              <span className={styles.historyTime}>
                                ⏱{Math.floor(item.timeElapsed / 60)}:
                                {String(item.timeElapsed % 60).padStart(2, "0")}
                              </span>
                            )}
                            {/* Actions */}
                            <div className={styles.historyActions}>
                              <Link
                                to={`/study/spanish/exam/${item.examId || item.exam?.id}?recordId=${item.id}`}
                                className={`btn btn-ghost btn-sm ${styles.historyBtnReview}`}
                              >
                                {t("exam.review_btn")}
                              </Link>
                              <button
                                onClick={() => handleDeleteClick(item.id)}
                                className={`btn btn-ghost btn-sm ${styles.historyBtnDelete}`}
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </>
              ))}

            {/* Vocabulary Units tab */}
            {dashTab === "vocab" && (
              <div>
                {vocabUnits.length === 0 ? (
                  <div className={styles.empty}>
                    <span style={{ fontSize: "2rem" }}>📖</span>
                    <p>Vocabulary will display here after exam page created.</p>
                  </div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: "0.45rem" }}>
                    {vocabUnits.map((unit) => (
                      <div
                        key={unit.id}
                        className={styles.historyRow}
                        style={{ cursor: "pointer", alignItems: "center" }}
                        onClick={() => navigate(`/study/spanish/vocabulary/${unit.id}`)}
                      >
                        <span style={{ fontSize: "1rem" }}>📖</span>
                        <span
                          style={{
                            flex: 1,
                            fontSize: "0.8rem",
                            fontWeight: 600,
                            color: "#1a2e1c",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {decodeURIComponent((unit.title || "").replace(/\+/g, " "))}
                        </span>
                        <span
                          style={{
                            fontSize: "0.68rem",
                            background: "#e8f5e9",
                            color: "#2e7d32",
                            padding: "0.15rem 0.45rem",
                            borderRadius: "9999px",
                            fontWeight: 700,
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          {unit.wordCount} 词
                        </span>
                        <span
                          style={{
                            fontSize: "0.68rem",
                            color: "#9ca3af",
                            whiteSpace: "nowrap",
                            flexShrink: 0,
                          }}
                        >
                          {new Date(unit.createdAt).toLocaleDateString([], {
                            month: "2-digit",
                            day: "2-digit",
                          })}
                        </span>
                        <span style={{ color: "#2f8956", fontSize: "0.75rem" }}>›</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Favorites tab */}
            {dashTab === "favorites" && (
              <FavoritesTab key="fav" t={t} subject={subject} styles={styles} />
            )}

          </div>
        </div>
      </div>

      {showPasswordModal &&
        portalTarget &&
        createPortal(
          <div className={styles.modalOverlay}>
            <div
              className={`${styles.modalContent} ${modalStatus === "error" ? styles.pinShakeAnim : ""
                }`}
            >
              <h3
                style={{
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  marginBottom: "0.5rem",
                }}
              >
                Enter Password
              </h3>
              <p
                style={{
                  fontSize: "0.85rem",
                  color: "#666",
                  marginBottom: "1.5rem",
                }}
              >
                Please enter the 6-digit password to generate an exam (valid for 3 minutes).
              </p>

              {/* 6-Digit Interactive PIN Container with Eye Button */}
              <div className={styles.pinInputWrapper}>
                {/* 6 Animated PIN Boxes Area */}
                <div
                  className={styles.pinBoxesRow}
                  onClick={() => passwordInputRef.current?.focus()}
                >
                  {/* Hidden native input with full keyboard, paste & navigation support */}
                  <input
                    ref={passwordInputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={examPassword}
                    onChange={(e) =>
                      setExamPassword(e.target.value.replace(/\D/g, ""))
                    }
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        examPassword.length === 6 &&
                        modalStatus !== "verifying" &&
                        modalStatus !== "success"
                      ) {
                        handlePasswordSubmit();
                      }
                    }}
                    className={styles.hiddenInputOverlay}
                    autoFocus
                  />

                  {Array.from({ length: 6 }).map((_, idx) => {
                    const isFilled = idx < examPassword.length;
                    const isActive = idx === examPassword.length;
                    const digit = examPassword[idx];

                    return (
                      <div
                        key={idx}
                        className={`${styles.pinBox} ${isFilled ? styles.pinBoxFilled : ""
                          } ${isActive ? styles.pinBoxActive : ""} ${modalStatus === "error" ? styles.pinBoxError : ""
                          } ${modalStatus === "success" ? styles.pinBoxSuccess : ""
                          }`}
                      >
                        {isFilled ? (
                          showExamPassword ? (
                            <span
                              key={`digit-${idx}-${digit}`}
                              className={styles.pinDigit}
                            >
                              {digit}
                            </span>
                          ) : (
                            <span
                              key={`bullet-${idx}`}
                              className={styles.pinBullet}
                            />
                          )
                        ) : isActive ? (
                          <span className={styles.pinBlinkingCursor} />
                        ) : null}
                      </div>
                    );
                  })}
                </div>

                {/* Eye Visibility Toggle Button (borderless) */}
                <button
                  type="button"
                  className={styles.pinEyeBtn}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowExamPassword((prev) => !prev);
                  }}
                  title={showExamPassword ? "Hide password" : "Show password"}
                  aria-label={showExamPassword ? "Hide password" : "Show password"}
                >
                  {showExamPassword ? <EyeOff size={21} /> : <Eye size={21} />}
                </button>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  justifyContent: "center",
                  marginBottom: "1rem",
                }}
              >
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowPasswordModal(false)}
                >
                  Cancel
                </button>
                <button
                  className={`btn btn-primary ${styles.pinSubmitBtn}`}
                  disabled={
                    examPassword.length !== 6 ||
                    modalStatus === "verifying" ||
                    modalStatus === "success"
                  }
                  onClick={handlePasswordSubmit}
                >
                  {modalStatus === "verifying" ? "Verifying..." : "Submit"}
                </button>
              </div>
              {modalStatus === "error" && (
                <p
                  style={{
                    color: "#E91E63",
                    fontWeight: "600",
                    fontSize: "0.8rem",
                    marginTop: "0.5rem",
                  }}
                >
                  Incorrect password, please try again.
                </p>
              )}
              {modalStatus === "success" && (
                <p
                  style={{
                    color: "#1e8e3e",
                    fontWeight: "600",
                    fontSize: "0.8rem",
                    marginTop: "0.5rem",
                  }}
                >
                  Success! Starting generation now...
                </p>
              )}
            </div>
          </div>,
          portalTarget,
        )}

      {generationFeedback &&
        portalTarget &&
        createPortal(
          <div className={styles.modalOverlay}>
            <div className={styles.progressModalContent}>
              {/* Title */}
              <h3 className={styles.progressModalTitle}>
                {genProgress >= 100
                  ? "Exam & Vocab Ready!"
                  : genProgress >= 85
                    ? "Building Exam Questions..."
                    : genProgress >= 55
                      ? "Compiling Vocabulary Bank..."
                      : genProgress >= 25
                        ? "Extracting Unit Vocab..."
                        : "Analyzing Document..."}
              </h3>

              {/* Progress Bar Track & Fill */}
              <div className={styles.progressTrackContainer}>
                <div className={styles.progressBarTrack}>
                  <div
                    className={styles.progressBarFill}
                    style={{
                      width: `${Math.min(100, Math.max(0, genProgress))}%`,
                    }}
                  />
                </div>

                {/* Progress Percentage Numbers */}
                <div className={styles.progressNumberRow}>
                  <span className={styles.progressStatusText}>
                    {genProgress >= 100 ? "Complete" : "Generating..."}
                  </span>
                  <span
                    className={`${styles.progressPercentText} ${genProgress >= 100 ? styles.done : ""
                      }`}
                  >
                    {Math.round(genProgress)}%
                  </span>
                </div>
              </div>

              {/* Milestones Step Indicator */}
              <div className={styles.progressMilestones}>
                {/* 1. Document / Scan */}
                <div
                  className={`${styles.milestoneItem} ${genProgress >= 25
                    ? styles.milestoneCompleted
                    : styles.milestoneActive
                    }`}
                >
                  <div className={styles.milestoneDot}>
                    {genProgress >= 25 ? "✓" : "1"}
                  </div>
                  <span className={styles.milestoneLabel}>Document</span>
                </div>

                {/* 2. Unit Vocab */}
                <div
                  className={`${styles.milestoneItem} ${genProgress >= 55
                    ? styles.milestoneCompleted
                    : genProgress >= 25
                      ? styles.milestoneActive
                      : ""
                    }`}
                >
                  <div className={styles.milestoneDot}>
                    {genProgress >= 55 ? "✓" : "2"}
                  </div>
                  <span className={styles.milestoneLabel}>Unit Vocab</span>
                </div>

                {/* 3. Vocab Bank */}
                <div
                  className={`${styles.milestoneItem} ${genProgress >= 85
                    ? styles.milestoneCompleted
                    : genProgress >= 55
                      ? styles.milestoneActive
                      : ""
                    }`}
                >
                  <div className={styles.milestoneDot}>
                    {genProgress >= 85 ? "✓" : "3"}
                  </div>
                  <span className={styles.milestoneLabel}>Vocab Bank</span>
                </div>

                {/* 4. Exam */}
                <div
                  className={`${styles.milestoneItem} ${genProgress >= 100
                    ? styles.milestoneCompleted
                    : genProgress >= 85
                      ? styles.milestoneActive
                      : ""
                    }`}
                >
                  <div className={styles.milestoneDot}>
                    {genProgress >= 100 ? "✓" : "4"}
                  </div>
                  <span className={styles.milestoneLabel}>Exam</span>
                </div>
              </div>

            </div>
          </div>,
          portalTarget,
        )}

      {deleteModalRecordId &&
        portalTarget &&
        createPortal(
          <div className={styles.modalOverlay}>
            <div
              className={`${styles.modalContent} ${deleteModalStatus === "error" ? styles.pinShakeAnim : ""
                }`}
            >
              <h3
                style={{
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  marginBottom: "0.5rem",
                  color: "#e53935",
                }}
              >
                Delete Record
              </h3>
              <p
                style={{
                  fontSize: "0.85rem",
                  color: "#666",
                  marginBottom: "1.5rem",
                }}
              >
                Please enter the 6-digit password same as used for generating the exam.
              </p>

              {/* 6-Digit Interactive PIN Container with Eye Button */}
              <div className={styles.pinInputWrapper}>
                {/* 6 Animated PIN Boxes Area */}
                <div
                  className={styles.pinBoxesRow}
                  onClick={() => deletePasswordInputRef.current?.focus()}
                >
                  {/* Hidden native input with full keyboard, paste & navigation support */}
                  <input
                    ref={deletePasswordInputRef}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={deletePassword}
                    onChange={(e) =>
                      setDeletePassword(e.target.value.replace(/\D/g, ""))
                    }
                    onKeyDown={(e) => {
                      if (
                        e.key === "Enter" &&
                        deletePassword.length === 6 &&
                        deleteModalStatus !== "deleting" &&
                        deleteModalStatus !== "success"
                      ) {
                        handleDeleteSubmit();
                      }
                    }}
                    className={styles.hiddenInputOverlay}
                    autoFocus
                  />

                  {Array.from({ length: 6 }).map((_, idx) => {
                    const isFilled = idx < deletePassword.length;
                    const isActive = idx === deletePassword.length;
                    const digit = deletePassword[idx];

                    return (
                      <div
                        key={idx}
                        className={`${styles.pinBox} ${isFilled ? styles.pinBoxFilled : ""
                          } ${isActive ? styles.pinBoxActive : ""} ${deleteModalStatus === "error" ? styles.pinBoxError : ""
                          } ${deleteModalStatus === "success"
                            ? styles.pinBoxSuccess
                            : ""
                          }`}
                      >
                        {isFilled ? (
                          showDeletePassword ? (
                            <span
                              key={`digit-${idx}-${digit}`}
                              className={styles.pinDigit}
                            >
                              {digit}
                            </span>
                          ) : (
                            <span
                              key={`bullet-${idx}`}
                              className={styles.pinBullet}
                            />
                          )
                        ) : isActive ? (
                          <span className={styles.pinBlinkingCursor} />
                        ) : null}
                      </div>
                    );
                  })}
                </div>

                {/* Eye Visibility Toggle Button (borderless) */}
                <button
                  type="button"
                  className={styles.pinEyeBtn}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowDeletePassword((prev) => !prev);
                  }}
                  title={showDeletePassword ? "Hide password" : "Show password"}
                  aria-label={showDeletePassword ? "Hide password" : "Show password"}
                >
                  {showDeletePassword ? <EyeOff size={21} /> : <Eye size={21} />}
                </button>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: "1rem",
                  justifyContent: "center",
                  marginBottom: "1rem",
                }}
              >
                <button
                  className="btn btn-secondary"
                  onClick={() => setDeleteModalRecordId(null)}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  style={{
                    backgroundColor: "#e53935",
                    borderColor: "#e53935",
                    color: "white",
                  }}
                  disabled={
                    deletePassword.length !== 6 ||
                    deleteModalStatus === "deleting" ||
                    deleteModalStatus === "success"
                  }
                  onClick={handleDeleteSubmit}
                >
                  {deleteModalStatus === "deleting" ? "Deleting..." : "Delete"}
                </button>
              </div>
              {deleteModalStatus === "error" && (
                <p
                  style={{
                    color: "#E91E63",
                    fontWeight: "bold",
                    fontSize: "0.9rem",
                    marginTop: "0.5rem",
                    textAlign: "center",
                  }}
                >
                  Incorrect password, please try again.
                </p>
              )}
              {deleteModalStatus === "success" && (
                <p
                  style={{
                    color: "#1e8e3e",
                    fontWeight: "bold",
                    fontSize: "0.9rem",
                    marginTop: "0.5rem",
                    textAlign: "center",
                  }}
                >
                  Deleted successfully!
                </p>
              )}
            </div>
          </div>,
          portalTarget,
        )}

      {/* Generation Error Modal */}
      {genError &&
        portalTarget &&
        createPortal(
          <div className={styles.modalOverlay}>
            <div
              className={styles.modalContent}
              style={{ maxWidth: "360px", textAlign: "center" }}
            >
              <div style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}>
                {genError === "busy" ? "⏳" : "⚠️"}
              </div>
              <h3
                style={{
                  fontSize: "1.1rem",
                  fontWeight: 700,
                  marginBottom: "0.5rem",
                  color: "#333",
                }}
              >
                {genError === "busy" ? "System Busy" : "Generation Failed"}
              </h3>
              <p
                style={{
                  fontSize: "0.85rem",
                  color: "#666",
                  lineHeight: 1.6,
                  marginBottom: "1.5rem",
                }}
              >
                {genError === "busy"
                  ? "The AI service is currently experiencing high demand. Please wait a moment and try again."
                  : "An error occurred while generating the exam. Please check your connection and try again."}
              </p>
              <button
                className="btn btn-primary"
                style={{ minWidth: "100px" }}
                onClick={() => setGenError(null)}
              >
                Got it
              </button>
            </div>
          </div>,
          portalTarget,
        )}
    </div>
  );
}

/* ── Vocabulary card sub-component ─────────────────────────────────────── */
// 1. 增强版的 POS 转换函数（不区分大小写，增加兜底）
// const getFullPos = (pos) => {
//   if (!pos) return "Noun"; // 如果字段为空，默认兜底或返回空字符串 ''

//   const cleanPos = pos.toString().trim().toLowerCase();

//   const map = {
//     n: "Noun",
//     "n.": "Noun",
//     noun: "Noun",
//     v: "Verb",
//     "v.": "Verb",
//     verb: "Verb",
//     adj: "Adjective",
//     "adj.": "Adjective",
//     adjective: "Adjective",
//     adv: "Adverb",
//     "adv.": "Adverb",
//     adverb: "Adverb",
//     phr: "Phrase",
//     "phr.": "Phrase",
//     phrase: "Phrase",
//     prep: "Preposition",
//     "prep.": "Preposition",
//     preposition: "Preposition",
//     conj: "Conjunction",
//     "conj.": "Conjunction",
//     conjunction: "Conjunction",
//     pron: "Pronoun",
//     "pron.": "Pronoun",
//     pronoun: "Pronoun",
//   };

//   // 如果在 map 里找不到，就把首字母转为大写输出
//   return map[cleanPos] || cleanPos.charAt(0).toUpperCase() + cleanPos.slice(1);
// };

// function VocabularyCard({ item, expanded, onToggle }) {
//   // 兼容不同的字段命名 (pos 或 partOfSpeech)
//   const posValue = item.pos || item.partOfSpeech || item.type;

//   return (
//     <div
//       onClick={onToggle}
//       className={`${styles.vocabularyCard} ${expanded ? styles.vocabularyCardExpanded : ""}`}
//     >
//       {/* 头部单行 */}
//       <div className={styles.vocabularyHeader}>
//         <span className={styles.vocabularySpanish}>
//           {item.spanish || item.word}
//         </span>
//         {/* 确保 POS 始终能渲染 */}
//         <span className={styles.vocabularyPos}>{getFullPos(posValue)}</span>
//       </div>

//       {/* 展开内容 */}
//       <div
//         className={`${styles.vocabularyDetails} ${expanded ? styles.vocabularyDetailsOpen : ""}`}
//       >
//         <div style={{ overflow: "hidden" }}>
//           <div className={styles.vocabularyDetailGrid}>
//             <div>
//               <span className={styles.vocabularyLabel}>ENGLISH</span>
//               <p>{item.english || item.meaning || "N/A"}</p>
//             </div>

//             {/* 中文展示：如果有中文则显示，没有时可显示提示或只展示英文 */}
//             <div>
//               <span className={styles.vocabularyLabel}>CHINESE</span>
//               <p>{item.chinese || item.translation || "暂无中文翻译"}</p>
//             </div>
//           </div>

//           {item.context && (
//             <div className={styles.vocabularyContextBox}>
//               <span className={styles.vocabularyLabel}>CONTEXT</span>
//               <p>"{item.context}"</p>
//             </div>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// }

/* ── Favorites sub-component ──────────────────────────────────────────────── */
function FavoritesTab({ t, subject, styles }) {
  const [favorites, setFavorites] = useState([]);
  const [pendingRemove, setPendingRemove] = useState({});

  useEffect(() => {
    (async () => {
      setFavorites(await getFavorites(subject));
    })();
  }, [subject]);

  const handleRemove = (id) => {
    const timer = setTimeout(async () => {
      await removeFavorite(id);
      setFavorites((prev) => prev.filter((f) => f.id !== id));
      setPendingRemove((prev) => {
        const n = { ...prev };
        delete n[id];
        return n;
      });
    }, 5000);
    setPendingRemove((prev) => ({ ...prev, [id]: timer }));
  };

  const handleUndo = (id) => {
    clearTimeout(pendingRemove[id]);
    setPendingRemove((prev) => {
      const n = { ...prev };
      delete n[id];
      return n;
    });
  };

  if (favorites.length === 0) {
    return (
      <div className={styles.empty}>
        <span style={{ fontSize: "2rem" }}>⭐</span>
        <p>{t("exam.favorites_empty")}</p>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      {favorites.map((fav) => {
        const isPending = !!pendingRemove[fav.id];
        return (
          <div
            key={fav.id}
            className={styles.historyItem}
            style={{ opacity: isPending ? 0.5 : 1, transition: "opacity 0.3s" }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                gap: "0.4rem",
              }}
            >
              <p
                style={{
                  fontSize: "0.8rem",
                  fontWeight: 600,
                  flex: 1,
                  margin: 0,
                }}
              >
                {fav.question}
              </p>
              {isPending ? (
                <button
                  onClick={() => handleUndo(fav.id)}
                  style={{
                    background: "none",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "0.7rem",
                    color: "var(--color-primary, #4caf50)",
                    padding: "2px 7px",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  ↩ Undo
                </button>
              ) : (
                <button
                  onClick={() => handleRemove(fav.id)}
                  style={{
                    background: "none",
                    border: "1px solid #ddd",
                    borderRadius: "6px",
                    cursor: "pointer",
                    fontSize: "0.7rem",
                    color: "#aaa",
                    padding: "2px 7px",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                  }}
                >
                  ✓ Got it
                </button>
              )}
            </div>
            {fav.type === "mc" && fav.options && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.2rem",
                }}
              >
                {fav.options.map((opt, i) => {
                  const letter = String.fromCharCode(65 + i);
                  const isCorrect = letter === fav.answer;
                  return (
                    <span
                      key={letter}
                      style={{
                        fontSize: "0.75rem",
                        color: isCorrect ? "#2e7d32" : "#888",
                        fontWeight: isCorrect ? 700 : 400,
                      }}
                    >
                      {letter}. {opt.replace(/^[A-D][.)]\s*/i, "")}{" "}
                      {isCorrect ? "✅" : ""}
                    </span>
                  );
                })}
              </div>
            )}
            {fav.type !== "mc" && fav.answer && (
              <p style={{ fontSize: "0.75rem", color: "#888", margin: 0 }}>
                💡 {fav.answer}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
